import type { FilterObject } from './filter-expression'

export enum RiskControlScope {
  Global = 'global',
  Account = 'account',
  Organization = 'organization',
  RiskGroup = 'risk-group',
}

export enum RiskControlType {
  Universe = 'universe',
  Exposure = 'exposure',
  Order = 'order',
  Attestation = 'attestation',
  Setting = 'setting',
}

/** An organization has at most one control of each setting for each scope. */
export enum SettingName {
  HolidayOrderEntry = 'holiday_order_entry',
}

export enum Remediation {
  Reject = 'reject',
  Flag = 'flag',
  None = 'none',
}

/** Absent means the control leaves marketplace visibility alone. */
export enum MarketplaceRestriction {
  Hide = 'hide',
}

export interface RiskControlResponse {
  applies_to_asset_classes: string[]
  control_id: string
  created_at: string
  archived_at?: string
  definition: string // Raw JSON string
  enabled: boolean
  entity_ids: string[]
  excluded_entity_ids: string[]
  filter?: RiskFilter
  name: string
  organization_id: string
  remediation: Remediation
  marketplace_restriction?: MarketplaceRestriction
  saved_by: string
  scope: RiskControlScope
  type: RiskControlType
  universe_parameters?: UniverseParameters
  exposure_settings?: ExposureSettings
  order_attributes?: OrderAttributes
  attestation_attributes?: AttestationAttributes
  setting_attributes?: SettingAttributes
  version_id: string
}

export interface HolidayOrderEntrySetting {
  denied: boolean
}

/** Present on setting controls only. The field named by `setting` holds its value. */
export type SettingAttributes = {
  setting: SettingName.HolidayOrderEntry
  holiday_order_entry: HolidayOrderEntrySetting
}

/** Present on attestation controls only. The service always serializes both flags. */
interface AttestationAttributes {
  attestation: AttestationRef
  applicable_to_buy_orders: boolean
  applicable_to_sell_orders: boolean
  parameters?: AttestationParameters
  account_condition?: AccountCondition
}

interface AttestationRef {
  id: string
}

export type RiskControlVolumesResponse = Record<
  string,
  {
    open: number
    closed: number
    total: number
  }
>

interface RiskFilter {
  filter: FilterObject
}

export interface UniverseParameters extends TradeHoldOptions {
  parameters: FilterObject
}

/** Daily volume limits, in dollars. Each one that is set is checked on its own. */
export interface ExposureSettings {
  open_volume_maximum?: number
  closed_volume_maximum?: number
  total_volume_maximum?: number
}

export type ExposureLimitKey = keyof ExposureSettings

export interface OrderAttributes {
  absolute_price_pop_maximum?: number
  absolute_price_pop_minimum?: number
  account_identifier_patterns?: string[]
  denomination_minimum?: number
  market_value_maximum?: number
  market_value_minimum?: number
  price_band_percentage_lower?: number
  price_band_percentage_upper?: number
  settlement_calendar_days_out_maximum?: number
  size_notional_maximum?: number
  size_notional_minimum?: number
  yield_to_maturity_percentage_maximum?: number
  yield_to_maturity_percentage_minimum?: number
  yield_to_worst_percentage_maximum?: number
  yield_to_worst_percentage_minimum?: number
}

export enum OrderAttributeKeys {
  AbsolutePricePopMaximum = 'absolute_price_pop_maximum',
  AbsolutePricePopMinimum = 'absolute_price_pop_minimum',
  AccountIdentifierPatterns = 'account_identifier_patterns',
  DenominationMinimum = 'denomination_minimum',
  MarketValueMaximum = 'market_value_maximum',
  MarketValueMinimum = 'market_value_minimum',
  PriceBandPercentageLower = 'price_band_percentage_lower',
  PriceBandPercentageUpper = 'price_band_percentage_upper',
  SettlementCalendarDaysOutMaximum = 'settlement_calendar_days_out_maximum',
  SizeNotionalMaximum = 'size_notional_maximum',
  SizeNotionalMinimum = 'size_notional_minimum',
  YieldToMaturityPercentageMaximum = 'yield_to_maturity_percentage_maximum',
  YieldToMaturityPercentageMinimum = 'yield_to_maturity_percentage_minimum',
  YieldToWorstPercentageMaximum = 'yield_to_worst_percentage_maximum',
  YieldToWorstPercentageMinimum = 'yield_to_worst_percentage_minimum',
}

/**
 * entity_ids depend on the scope of the controls (which it's passed with).
 *  - If global, it's not actually needed (it's implied that it's user's org)
 *  - If child org(s), it's org ID
 *  If account(s), it's account IDs
 */
interface ScopeDef {
  scope: RiskControlScope
  entity_ids?: string[]
  excluded_entity_ids?: string[]
}

// Control Filter
interface ControlFilter {
  filter?: FilterObject
}

export interface TradeHoldOptions {
  applicable_to_buy_orders?: boolean // Risky Buinsiness defaults this to true
  applicable_to_sell_orders?: boolean // defaults this to false
  applicable_to_holdings?: boolean // defaults this to false
}

export interface UniverseConstraint
  extends ControlFilter,
    UniverseParameters,
    TradeHoldOptions {
  type: RiskControlType.Universe
}

export interface ExposureLimit extends ControlFilter, ExposureSettings {
  type: RiskControlType.Exposure
}
export interface OrderValidation extends ControlFilter, OrderAttributes {
  type: RiskControlType.Order
}

/** Order-parameter triggers that narrow which orders the attestation applies to. */
export interface AttestationParameters {
  below_de_minimis?: boolean
}

/**
 * Narrows which accounts on an order the attestation applies to. An omitted
 * field is not tested; true and false each select only the accounts matching
 * that value, and an account the order says nothing about matches neither.
 */
export interface AccountCondition {
  is_discretionary?: boolean
  is_solicited?: boolean
  is_natural_person?: boolean
}

/** Exactly one attestation per control, so requiring several takes several controls. */
export interface AttestationRequirement extends ControlFilter {
  type: RiskControlType.Attestation
  attestation: AttestationRef
  applicable_to_buy_orders?: boolean
  applicable_to_sell_orders?: boolean
  parameters?: AttestationParameters
  account_condition?: AccountCondition
}

/** risky-business stores the none remediation for a setting control. */
type SettingControl = SettingAttributes & {
  type: RiskControlType.Setting
}

/** The control types that the create and edit dialogs build. */
export type FilterableControlUnion =
  | UniverseConstraint
  | ExposureLimit
  | OrderValidation
  | AttestationRequirement

export type ControlUnion = FilterableControlUnion | SettingControl

// Response when creating/updating a risk control
export interface CreateRiskControlResponse {
  id: string
}

export interface UpdateRiskControlResponse extends CreateRiskControlResponse {}

// Request for creating/updating a risk control
export interface CreateRiskControlRequest {
  name: string
  applies_to: ScopeDef
  applies_to_asset_classes?: string[]
  enabled?: boolean
  control: ControlUnion
  remediation: Remediation
  marketplace_restriction?: MarketplaceRestriction
}

/** Narrows the list to the given control types; all types when absent. */
export interface ListRiskControlsArgs {
  types?: RiskControlType[]
}

// Request for deleting a risk control
export interface DeleteRiskControlRequest {
  control_id: string
}

// Update this interface to match the API expectation
export interface UpdateRiskControlRequest {
  id: string
  control: CreateRiskControlRequest
}

// Interface for generateRiskControlRequest parameters
export interface GenerateRiskControlParams {
  name: string
  control: ControlUnion
  filter: FilterObject | null
  scope: RiskControlScope
  entityIds?: string[]
  excludedEntityIds?: string[]
  remediation: Remediation
  marketplaceRestriction: MarketplaceRestriction | null
  assetClasses?: string[]
}
